import { CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Row, Select, Tag } from 'antd';
import React, { useMemo } from 'react';
import { useGroupedFieldOptions } from '../../../../../hooks/useGroupedFieldOptions';
import { CONDITION_OPERATORS } from '../../../../../utils/const/mapping';
import ValueInput from './valueInputs';

const ConditionCard = ({
  index = 0,
  condition = {},
  onFieldChange,
  onOperatorChange,
  onValueChange,
  handleDeleteCondition,
  _isLogicalCondition,
  fieldNamePrefix = [], // Préfixe pour les noms de champs (ex: ['chain', 'steps', 0, 'condition'])
}) => {
  const { groupedFieldOptions, rawAttributes } = useGroupedFieldOptions();

  const operatorCategory = useMemo(() => {
    return CONDITION_OPERATORS.find((op) => op.value === condition.operator)
      ?.category;
  }, [condition.operator]);

  const groupedOperators = useMemo(() => {
    const categoryLabels = {
      string: 'Opérateurs de texte',
      numeric: 'Opérateurs numériques',
      list: 'Opérateurs de liste',
      logical: 'Opérateurs logiques',
    };

    const grouped = CONDITION_OPERATORS.reduce((acc, operator) => {
      const category = operator.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({
        label: operator.label,
        value: operator.value,
      });
      return acc;
    }, {});

    // Ordre des catégories : texte en premier
    const order = ['string', 'numeric', 'list', 'logical'];
    return order
      .filter((key) => grouped[key])
      .map((key) => ({
        label: categoryLabels[key] || key,
        options: grouped[key],
      }));
  }, []);

  const fieldInputValue = Form.useWatch([...fieldNamePrefix, 'field']);

  // Trouver le champ source sélectionné dans rawAttributes
  const selectedSourceField = useMemo(() => {
    if (!fieldInputValue || !rawAttributes) return null;
    return rawAttributes.find((attr) => attr.name === fieldInputValue);
  }, [fieldInputValue, rawAttributes]);

  // Si l'attribut source (interne) a une value_list, on affiche un Select
  const effectiveOperatorCategory = useMemo(() => {
    if (selectedSourceField?.value_list) {
      return 'selector';
    }
    return operatorCategory;
  }, [selectedSourceField, operatorCategory]);

  // Options pour le SelectorInput (valeurs internes)
  const selectorOptions = useMemo(() => {
    if (!selectedSourceField?.value_list) return [];
    return selectedSourceField.value_list.map((item) => ({
      code: item.en,
      label: item.fr || item.en,
    }));
  }, [selectedSourceField]);

  return (
    <Card
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircleOutlined style={{ color: '#52C41A' }} />
          Condition {index + 1}
        </span>
      }
      style={{
        borderColor: '#52C41A',
        backgroundColor: '#52C41A12',
      }}
    >
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Champ"
            name={[...fieldNamePrefix, 'field']}
            rules={[
              { required: true, message: 'Veuillez sélectionner un champ' },
            ]}
            initialValue={
              condition?.field?.length > 0 ? condition?.field : undefined
            }
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder="Champ source"
              options={groupedFieldOptions}
              onChange={onFieldChange}
              optionRender={(option) => (
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {option.label}
                  {option.data.metric && <Tag>{option.data.metric}</Tag>}
                </span>
              )}
            />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item
            label="Opérateur"
            name={[...fieldNamePrefix, 'operator']}
            rules={[
              { required: true, message: 'Veuillez sélectionner un opérateur' },
            ]}
            initialValue={condition?.operator}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="Sélectionnez une condition"
              options={groupedOperators}
              onChange={onOperatorChange}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Valeur"
            name={[...fieldNamePrefix, 'value']}
            rules={[{ required: true, message: 'Veuillez saisir une valeur' }]}
            initialValue={condition?.value}
          >
            <ValueInput
              operatorCategory={effectiveOperatorCategory}
              onChange={onValueChange}
              selectorOptions={selectorOptions}
            />
          </Form.Item>
        </Col>
        {handleDeleteCondition && (
          <Col
            span={1}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'end',
              paddingTop: 30,
            }}
          >
            <Form.Item
              name={[...fieldNamePrefix, 'uuid']}
              initialValue={condition.uuid}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteCondition(condition.uuid)}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default ConditionCard;
